import { z } from 'zod';

/**
 * Push notifications a user can turn on or off (Configurações). Stored in
 * `User.preferences.notifications`; a missing key means its default.
 */
export const NOTIFICATION_KINDS = ['botStarted', 'botTarget', 'botStopLoss', 'botStopped', 'botTrades', 'tradeResults', 'deposits', 'support', 'payouts', 'broadcast'] as const;
export type NotificationKind = (typeof NOTIFICATION_KINDS)[number];
export type NotificationPrefs = Record<NotificationKind, boolean>;

export const DEFAULT_NOTIFICATION_PREFS: NotificationPrefs = {
  botStarted: true,
  botTarget: true,
  botStopLoss: true,
  botStopped: true,
  // Every bot entry settles in seconds: off unless the user asks for it.
  botTrades: false,
  tradeResults: true,
  deposits: true,
  // Answers in Sugestões (for admins: new tickets and user replies).
  support: true,
  // Affiliates: the team recorded the payment of their commissions (with the receipt).
  payouts: true,
  // Messages the team sends from Admin › Disparo de notificação.
  broadcast: true,
};

/** Results the open app already shows: skipped on the device that is on screen. */
export const QUIET_WHEN_VISIBLE: ReadonlySet<NotificationKind> = new Set(['botTrades', 'tradeResults', 'deposits']);

export function notificationPrefsOf(preferences: unknown): NotificationPrefs {
  const raw = (preferences as { notifications?: Record<string, unknown> } | null | undefined)?.notifications ?? {};
  const prefs = { ...DEFAULT_NOTIFICATION_PREFS };
  for (const k of NOTIFICATION_KINDS) if (typeof raw[k] === 'boolean') prefs[k] = raw[k] as boolean;
  return prefs;
}

export const notificationPrefsSchema = z.object(Object.fromEntries(NOTIFICATION_KINDS.map((k) => [k, z.boolean().optional()])) as Record<NotificationKind, z.ZodOptional<z.ZodBoolean>>);

/** Random id a browser keeps for itself: ties its push subscription to its on-screen presence. */
export const deviceIdSchema = z.string().regex(/^[A-Za-z0-9-]{8,64}$/);

export const pushSubscribeSchema = z.object({
  endpoint: z.url().max(1000),
  keys: z.object({ p256dh: z.string().min(16).max(200), auth: z.string().min(8).max(100) }),
  deviceId: deviceIdSchema,
});

/** Payload the service worker turns into a system notification. */
export interface PushPayload {
  title: string;
  body: string;
  /** Page opened on tap. */
  url: string;
  /** Same tag replaces the previous notification instead of stacking. */
  tag?: string;
}
